import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux/es/hooks/useSelector";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend as RechartsLegend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import { API_ENDPOINT } from "../../../services/api/resources";
import classes from "../../TabStyles/DashboardTab.module.css";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const T = {
  bg: "#F8FAFC",
  surface: "#FFFFFF",
  border: "rgba(226,232,240,0.76)",
  text: "#0F172A",
  textSec: "#334155",
  muted: "#161617",
  brand: "#4F7CFF",
  info: "#0EA5E9",
  success: "#24C8A5",
  warning: "#F5A524",
  danger: "#EF4444",
  purple: "#7C3AED",
  teal: "#14B8A6",
  pink: "#DB2777",
  fontBody: "GilroyRegular",
  fontSemiBold: "GilroySemiBold",
  fontDisplay: "GilroyBold",
};

const STATUS_COLORS = ["#24C8A5", "#F5A524", "#EF4444", "#7C3AED", "#64748B"];
const FIELD_COLORS = ["#4F7CFF", "#24C8A5", "#F5A524", "#EF4444"];
const ALL_MONTHS = "all";
const DASH = "\u2014";
const toNumber = (value) => {
  const number = Number(String(value ?? 0).replace(/,/g, ""));
  return Number.isFinite(number) ? number : 0;
};

const isPresent = (value) => {
  if (value === 0) return true;
  if (value === false) return true;
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

const toPercent = (part, total) => {
  const base = toNumber(total);
  if (!base) return "0%";
  return `${Math.round((toNumber(part) / base) * 100)}%`;
};

const formatNumber = (value) =>
  new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(toNumber(value));

const formatPreciseCurrency = (value, currency = "INR") =>
  isPresent(value)
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "INR",
        maximumFractionDigits: 0,
      }).format(toNumber(value))
    : DASH;

const formatCurrency = (value, currency = "INR") => {
  const amount = toNumber(value);
  if (amount >= 10000000) return `${currency} ${(amount / 10000000).toFixed(2)} Cr`;
  if (amount >= 100000) return `${currency} ${(amount / 100000).toFixed(2)} L`;
  return `${currency} ${formatNumber(amount)}`;
};

const titleCase = (value = "") =>
  String(value)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\w\S*/g, (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());

const monthLabel = (row = {}) =>
  `${row.month_name || row.month || ""}${row.year ? ` ${String(row.year).slice(-2)}` : ""}`.trim();

const buildMonthRows = (rows = [], mapper = (row) => row) =>
  (Array.isArray(rows) ? rows : []).map((row) => ({
    label: monthLabel(row),
    year: toNumber(row.year),
    month: toNumber(row.month),
    monthName: row.month_name || "",
    ...mapper(row),
  }));

const objectToRows = (source = {}, labelKey = "name", valueKey = "value") =>
  Object.entries(source || {}).map(([key, value]) => ({
    [labelKey]: titleCase(key),
    [valueKey]: toNumber(value),
    rawKey: key,
  }));

const topRows = (rows = [], valueKey = "value", limit = 8) =>
  [...rows].sort((a, b) => toNumber(b?.[valueKey]) - toNumber(a?.[valueKey])).slice(0, limit);

const asArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];
  return [];
};

const parseJsonSafely = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  if (typeof value !== "string") return {};
  try {
    return JSON.parse(value);
  } catch (error) {
    return {};
  }
};

const getFirstDefinedValue = (...values) => {
  for (const value of values) {
    if (isPresent(value)) return value;
  }
  return undefined;
};

const displayValue = (value, options = {}) => {
  const { currency, forceCurrency = false } = options;
  if (!isPresent(value)) return DASH;
  if (forceCurrency) return formatPreciseCurrency(value, currency || "INR");
  if (Array.isArray(value)) return value.length ? value.map((item) => displayValue(item)).join(", ") : DASH;
  if (typeof value === "object") {
    const flatValues = Object.entries(value)
      .filter(([, entry]) => isPresent(entry) && typeof entry !== "object")
      .map(([key, entry]) => `${normalizeLabel(key)}: ${entry}`);
    return flatValues.length ? flatValues.join(", ") : DASH;
  }
  return String(value);
};

const getObjectEntries = (value = {}) =>
  Object.entries(value || {}).filter(([, entry]) => isPresent(entry));

const normalizeLabel = (value = "") =>
  titleCase(String(value).replace(/\bpo\b/gi, "PO").replace(/\bgrn\b/gi, "GRN").replace(/\butr\b/gi, "UTR").replace(/\brrn\b/gi, "RRN"));

const formatDateValue = (value) => {
  if (!isPresent(value)) return DASH;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
};

const formatPercentValue = (value) => {
  if (!isPresent(value)) return DASH;
  const normalized = String(value).trim();
  return normalized.includes("%") ? normalized : `${normalized}%`;
};

const formatMonthPeriod = (value) => {
  if (!isPresent(value)) return DASH;
  const normalized = String(value);
  const match = normalized.match(/^(\d{4})-(\d{2})$/);
  if (!match) return normalized;
  const [, year, month] = match;
  const date = new Date(`${year}-${month}-01T00:00:00`);
  if (Number.isNaN(date.getTime())) return normalized;
  return new Intl.DateTimeFormat("en-IN", { month: "short", year: "numeric" }).format(date);
};

const getDocumentLineItems = (record = {}) =>
  Array.isArray(record?.line_items)
    ? record.line_items
    : Array.isArray(record?.items)
      ? record.items
      : [];

const getDocumentHeaderFields = (record = {}) =>
  [
    ["document_no", "Document No", getFirstDefinedValue(record?.document_no, record?.invoice_number, record?.invoice_no, record?.po_number, record?.purchase_order_no, record?.grn_number, record?.grn_no, record?.payment_advice_number)],
    ["date", "Date", getFirstDefinedValue(record?.date, record?.invoice_date, record?.po_date, record?.grn_date, record?.transaction_date)],
    ["vendor_name", "Vendor Name", getFirstDefinedValue(record?.vendor_name, record?.vendor, record?.supplier_name)],
    ["amount", "Amount", getFirstDefinedValue(record?.amount, record?.total_amount, record?.invoice_total_amount, record?.grand_total, record?.paid_amount)],
    ["status", "Status", record?.status],
  ].filter(([, , value]) => isPresent(value));

const getImportantPaymentSummaryRows = (summary = {}) =>
  [
    ["payment_status", "Payment Status", summary?.payment_status || summary?.status],
    ["record_count", "Records", summary?.record_count || summary?.payment_advance_record_count],
    ["document_count", "Documents", summary?.document_count || summary?.payment_advance_document_count],
    ["invoice_total_amount", "Invoice Value", summary?.invoice_total_amount || summary?.payment_advance_invoice_total_amount, true],
    ["paid_amount", "Paid Amount", summary?.paid_amount || summary?.payment_advance_total_amount || summary?.total_amount, true],
    ["outstanding_amount", "Outstanding Amount", summary?.outstanding_amount || summary?.payment_advance_outstanding_amount, true],
    ["payment_coverage_percent", "Payment Coverage", isPresent(summary?.payment_coverage_percent) ? formatPercentValue(summary.payment_coverage_percent) : summary?.coverage_percent],
    ["advance_percentage", "Advance Percentage", isPresent(summary?.advance_percentage) ? formatPercentValue(summary.advance_percentage) : undefined],
    ["payment_modes", "Payment Modes", summary?.payment_modes || Object.keys(summary?.payment_mode_breakup || {})],
    ["transaction_dates", "Transaction Dates", summary?.transaction_dates],
  ]
    .filter(([, , value]) => isPresent(value))
    .map(([key, label, value, forceCurrency]) => ({ key, label, value, forceCurrency }));

const getImportantPaymentAdviceRows = (record = {}) =>
  [
    ["payment_advice_number", "Payment Advice No", record?.payment_advice_number || record?.advice_number],
    ["po_number", "PO Number", record?.po_number || record?.purchase_order_no],
    ["amount", "Amount", record?.amount || record?.paid_amount || record?.total_amount, true],
    ["payment_product", "Payment Product", record?.payment_product],
    ["reference", "UTR / RRN / Reference", record?.utr_rrn_no || record?.utr_rrn || record?.utr || record?.rrn || record?.reference || record?.reference_no || record?.transaction_reference],
    ["transaction_date", "Transaction Date", record?.transaction_date],
    ["beneficiary", "Beneficiary", record?.beneficiary || record?.beneficiary_name],
    ["remitter", "Remitter", record?.remitter || record?.remitter_name],
    ["payment_mode", "Payment Mode", record?.payment_mode || record?.mode || record?.payment_product],
    ["status", "Status", record?.status],
  ]
    .filter(([, , value]) => isPresent(value))
    .map(([key, label, value, forceCurrency]) => ({ key, label, value, forceCurrency }));

const lineItemColumns = [
  ["description", "Description", (item) => item?.description || item?.item_name || item?.particulars],
  ["part_number", "Part No", (item) => item?.part_number || item?.part_no || item?.item_code],
  ["quantity", "Qty", (item) => item?.quantity || item?.qty],
  ["uom", "UOM", (item) => item?.uom || item?.unit],
  ["rate", "Rate", (item) => item?.rate || item?.unit_price],
  ["amount", "Amount", (item) => item?.amount || item?.line_amount || item?.taxable_amount],
  ["status", "Status", (item) => item?.status],
];

const LineItemsTable = ({ items = [], currency }) => (
  <div style={{ overflowX: "auto", maxWidth: "100%", border: `1px solid ${T.border}`, borderRadius: 12 }}>
    <table style={{ width: "100%", minWidth: 680, borderCollapse: "collapse", background: "#fff" }}>
      <thead>
        <tr style={{ background: "linear-gradient(90deg, rgba(79,124,255,0.14), rgba(14,165,233,0.1))" }}>
          {lineItemColumns.map(([, label]) => (
            <th key={label} style={{ padding: "12px", color: T.text, fontFamily: T.fontDisplay, fontSize: 13, textAlign: "left", borderBottom: `1px solid ${T.border}` }}>
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => (
          <tr key={`line-item-${index}`} style={{ background: index % 2 ? "#F8FAFC" : "#fff" }}>
            {lineItemColumns.map(([key, , getter]) => {
              const value = getter(item);
              const isCurrency = ["rate", "amount"].includes(key);
              return (
                <td key={key} style={{ padding: "11px 12px", color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 13, borderBottom: "1px solid rgba(226,232,240,0.65)", overflowWrap: "anywhere", verticalAlign: "top" }}>
                  {displayValue(value, { currency, forceCurrency: isCurrency && isPresent(value) })}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const getRecordStatusTone = (value = "") => {
  const normalized = String(value || "").toLowerCase();
  if (normalized.includes("full") || normalized.includes("match") || normalized.includes("paid") || normalized.includes("complete")) {
    return { bg: "rgba(36,200,165,0.12)", color: T.success, border: "rgba(36,200,165,0.28)" };
  }
  if (normalized.includes("partial") || normalized.includes("progress")) {
    return { bg: "rgba(245,165,36,0.12)", color: T.warning, border: "rgba(245,165,36,0.28)" };
  }
  if (normalized.includes("unpaid") || normalized.includes("mismatch") || normalized.includes("error") || normalized.includes("blocked")) {
    return { bg: "rgba(239,68,68,0.12)", color: T.danger, border: "rgba(239,68,68,0.26)" };
  }
  return { bg: "rgba(79,124,255,0.1)", color: T.brand, border: "rgba(79,124,255,0.22)" };
};

const StatusBadge = ({ label, value }) => {
  const tone = getRecordStatusTone(value);
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        borderRadius: 999,
        border: `1px solid ${tone.border}`,
        background: tone.bg,
        color: tone.color,
        padding: "6px 10px",
        fontFamily: T.fontSemiBold,
        fontSize: 12,
        whiteSpace: "normal",
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      {label ? <span style={{ opacity: 0.72 }}>{label}</span> : null}
      <span style={{ overflowWrap: "anywhere" }}>{displayValue(value)}</span>
    </div>
  );
};

const SummaryValueCard = ({ title, value, note, accent = T.brand, currency, forceCurrency = false, onClick }) => (
  <div
    onClick={onClick}
    style={{
      border: `1px solid ${T.border}`,
      borderRadius: 14,
      background: "linear-gradient(145deg, #FFFFFF 0%, #F8FBFF 58%, rgba(79,124,255,0.08) 100%)",
      padding: "16px 16px 14px",
      boxShadow: "0px 14px 30px rgba(15,23,42,0.08)",
      minHeight: 110,
      display: "grid",
      gap: 10,
      alignContent: "space-between",
      cursor: onClick ? "pointer" : "default",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <span style={{ position: "absolute", right: -18, top: -18, width: 72, height: 72, borderRadius: "50%", background: accent, opacity: 0.12 }} />
    <div style={{ color: T.muted, fontFamily: T.fontDisplay, fontSize: 16 }}>{title}</div>
    <div style={{ color: accent, fontFamily: T.fontDisplay, fontSize: 24, lineHeight: 1.15, wordBreak: "break-word" }}>
      {displayValue(value, { currency, forceCurrency })}
    </div>
    {note ? <div style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 14 }}>{note}</div> : null}
  </div>
);

const ProgressBar = ({ value = 0, color = T.brand }) => (
  <div style={{ height: 8, borderRadius: 999, background: "rgba(226,232,240,0.9)", overflow: "hidden" }}>
    <div
      style={{
        width: `${Math.max(0, Math.min(100, toNumber(value)))}%`,
        height: "100%",
        borderRadius: 999,
        background: color,
      }}
    />
  </div>
);

const KeyValueGrid = ({ rows = [], columns = "repeat(auto-fit, minmax(min(180px, 100%), 1fr))", currency, currencyKeys = [] }) => (
  <div style={{ display: "grid", gridTemplateColumns: columns, gap: 12, minWidth: 0, maxWidth: "100%" }}>
    {rows.map((row, index) => {
      const isCurrency = row.forceCurrency || currencyKeys.includes(row.key);
      return (
        <div
          key={`${row.label}-${index}`}
          style={{
            padding: "12px 13px",
            borderRadius: 12,
            border: "1px solid #D9E8F7",
            background: "linear-gradient(135deg, #F8FBFF 0%, #EEF6FF 55%, #E6F2FF 100%)",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <div style={{ color: T.muted, fontFamily: T.fontDisplay, fontSize: 15, marginBottom: 6 }}>{row.label}</div>
          <div style={{ color: T.text, fontFamily: T.fontBody, fontSize: 14, lineHeight: 1.45, overflowWrap: "anywhere", wordBreak: "break-word" }}>
            {displayValue(row.value, { currency, forceCurrency: isCurrency })}
          </div>
        </div>
      );
    })}
  </div>
);

const DetailAccordion = ({
  title,
  subtitle,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <details
      open={open}
      onToggle={(e) => setOpen(e.currentTarget.open)}
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        background: "#fff",
        overflow: "hidden",
      }}
    >
      <summary
        style={{
          listStyle: "none",
          cursor: "pointer",
          padding: "14px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              color: T.text,
              fontFamily: T.fontDisplay,
              fontSize: 16,
              overflowWrap: "anywhere",
            }}
          >
            {title}
          </div>

          {subtitle ? (
            <div
              style={{
                ...tooltipTextStyle,
                marginTop: 4,
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>

        <div
          style={{
            color: T.brand,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {open ? (
            <KeyboardArrowUpIcon
              sx={{
                fontSize: 26,
                color: T.brand,
              }}
            />
          ) : (
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 26,
                color: T.brand,
              }}
            />
          )}
        </div>
      </summary>

      <div
        style={{
          borderTop: `1px solid ${T.border}`,
          padding: 16,
          minWidth: 0,
          overflowX: "hidden",
        }}
      >
        {children}
      </div>

      <style>
        {`
          details > summary::-webkit-details-marker {
            display: none;
          }

          details > summary::marker {
            content: "";
          }
        `}
      </style>
    </details>
  );
};

const ResultJsonSection = ({ title, records = [], currency, docPageCount }) => {
  const rows = asArray(records);
  if (!rows.length) return null;

  return (
    <DetailAccordion
      title={title}
      subtitle={`${formatNumber(rows.length)} ${rows.length === 1 ? "record" : "records"}`}
    >
      <div style={{ display: "grid", gap: 14 }}>
        {rows.map((record, index) => {
          const headers = getDocumentHeaderFields(record).map(([key, label, value]) => ({
            key,
            label,
            value,
            forceCurrency: /amount|value|rate/i.test(key),
          }));
          const lineItems = getDocumentLineItems(record);

          return (
            <div
              key={`${title}-${index}`}
              style={{
                border: `1px solid ${T.border}`,
                borderRadius: 14,
                background: "#F8FAFC",
                padding: 14,
                display: "grid",
                gap: 14,
              }}
            >
              <KeyValueGrid
                rows={[
                  ...headers,
                  ...(isPresent(docPageCount) ? [{ key: "doc_page_count", label: "Doc Page Count", value: docPageCount }] : []),
                ]}
                currency={currency}
                currencyKeys={["invoice_total_amount", "invoice_amount", "line_amount", "amount", "taxable_amount", "paid_amount", "outstanding_amount", "rate"]}
              />
              {lineItems.length ? (
                <div style={{ display: "grid", gap: 10 }}>
                  <div style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 16 }}>Line Items</div>
                  <LineItemsTable items={lineItems} currency={currency} />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </DetailAccordion>
  );
};

const ItemComparisonCard = ({ item = {}, currency }) => {
  const status = getFirstDefinedValue(
    item?.comparison_status,
    item?.status,
    item?.overall_status,
  );
  const reasons = asArray(item?.reasons || item?.mismatch_reasons);

  return (
    <div
      style={{
        border: `1px solid ${T.border}`,
        borderRadius: 14,
        background: "#fff",
        padding: 14,
        display: "grid",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <div style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 16 }}>
            {displayValue(getFirstDefinedValue(item?.description, item?.item_name, item?.po?.item_name, item?.invoice?.item_name, item?.grn?.item_name))}
          </div>
          <div style={{ ...tooltipTextStyle, marginTop: 4 }}>
            Part Number: {displayValue(getFirstDefinedValue(item?.part_number, item?.po?.part_number, item?.invoice?.part_number, item?.grn?.part_number))}
          </div>
        </div>
        {isPresent(status) ? <StatusBadge value={typeof status === "object" ? JSON.stringify(status) : status} /> : null}
      </div>

      <KeyValueGrid
        rows={[
          { key: "po_quantity", label: "PO Quantity", value: getFirstDefinedValue(item?.po_quantity, item?.po?.quantity) },
          { key: "grn_quantity", label: "GRN Quantity", value: getFirstDefinedValue(item?.grn_quantity, item?.grn?.quantity) },
          { key: "invoice_quantity", label: "Invoice Quantity", value: getFirstDefinedValue(item?.invoice_quantity, item?.invoice?.quantity) },
          { key: "po_rate", label: "PO Rate", value: getFirstDefinedValue(item?.po_rate, item?.po?.rate), forceCurrency: true },
          { key: "invoice_rate", label: "Invoice Rate", value: getFirstDefinedValue(item?.invoice_rate, item?.invoice?.rate), forceCurrency: true },
          { key: "po_line_amount", label: "PO Line Amount", value: getFirstDefinedValue(item?.amount_comparison?.po_line_amount, item?.po?.line_amount), forceCurrency: true },
          { key: "invoice_line_amount", label: "Invoice Line Amount", value: getFirstDefinedValue(item?.amount_comparison?.invoice_line_amount, item?.invoice?.line_amount), forceCurrency: true },
          { key: "amount_difference", label: "Amount Difference", value: item?.amount_comparison?.amount_difference, forceCurrency: true },
          { key: "tolerance", label: "Tolerance", value: getFirstDefinedValue(item?.tolerance, item?.amount_comparison?.tolerance) },
          { key: "comparison_status", label: "Comparison Status", value: displayValue(status) },
        ].filter((row) => isPresent(row.value))}
        currency={currency}
      />

      {item?.comparison_status && typeof item.comparison_status === "object" ? (
        <KeyValueGrid
          rows={getObjectEntries(item.comparison_status).map(([key, value]) => ({
            key,
            label: normalizeLabel(key),
            value,
          }))}
          currency={currency}
        />
      ) : null}

      {reasons.length ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {reasons.map((reason, index) => (
            <StatusBadge key={`${reason}-${index}`} value={reason} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const DocumentCard = ({ file, docType, status, docPageCount }) => (
  <div
    style={{
      border: `1px solid ${T.border}`,
      borderRadius: 14,
      background: "#fff",
      padding: 14,
      display: "grid",
      gap: 10,
    }}
  >
    <div style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 16, wordBreak: "break-word" }}>{displayValue(file)}</div>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {isPresent(docType) ? <StatusBadge label="Doc" value={docType} /> : null}
      {isPresent(status) ? <StatusBadge label="Status" value={status} /> : null}
      {isPresent(docPageCount) ? <StatusBadge label="Pages" value={docPageCount} /> : null}
    </div>
  </div>
);

const ReconciliationRecordCard = ({ record = {}, currency, onViewDetails }) => {
  const paymentSummary =
    record?.payment_advance_summary ||
    record?.payment_advice_summary ||
    {};
  const paymentModes = Array.isArray(record?.payment_modes)
    ? record.payment_modes
    : Array.isArray(paymentSummary?.payment_modes)
      ? paymentSummary.payment_modes
      : getObjectEntries(paymentSummary?.payment_mode_breakup).map(([key]) => key);
  const transactionDates = Array.isArray(record?.transaction_dates)
    ? record.transaction_dates
    : Array.isArray(paymentSummary?.transaction_dates)
      ? paymentSummary.transaction_dates
      : [getFirstDefinedValue(record?.transaction_date, paymentSummary?.transaction_date)].filter(isPresent);
  const coverage = getFirstDefinedValue(record?.payment_coverage_percent, paymentSummary?.payment_coverage_percent, paymentSummary?.coverage_percent, 0);

  return (
    <div
      style={{
        border: "1px solid rgba(79,124,255,0.18)",
        borderRadius: 16,
        background: "linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%)",
        padding: 0,
        boxShadow: "0px 10px 24px rgba(15,23,42,0.07)",
        display: "grid",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      <div
        style={{
          padding: "12px 14px",
          background: "linear-gradient(90deg, rgba(79,124,255,0.12), rgba(36,200,165,0.08))",
          borderBottom: "1px solid rgba(226,232,240,0.72)",
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "flex-start",
          minWidth: 0,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ color: T.brand, fontFamily: T.fontDisplay, fontSize: 16, overflowWrap: "anywhere" }}>
            {displayValue(record?.match_code)}
          </div>
          <div style={{ ...tooltipTextStyle, marginTop: 3, color: T.textSec }}>
            {displayValue(record?.vendor_name)}
          </div>
          <div style={{ color: T.muted, fontFamily: T.fontSemiBold, fontSize: 12, marginTop: 2, overflowWrap: "anywhere" }}>
            PO {displayValue(record?.purchase_order_no)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {isPresent(record?.status) ? <StatusBadge value={record.status} /> : null}
          {isPresent(record?.matching_mode) ? <StatusBadge label="Mode" value={record.matching_mode} /> : null}
          {isPresent(record?.payment_status) ? <StatusBadge label="Payment" value={record.payment_status} /> : null}
        </div>
      </div>

      <div style={{ padding: 14, display: "grid", gap: 10, minWidth: 0 }}>
        <KeyValueGrid
          rows={[
            { key: "invoice_total_amount", label: "Invoice Value", value: record?.invoice_total_amount, forceCurrency: true },
            { key: "paid_amount", label: "Paid Amount", value: record?.paid_amount, forceCurrency: true },
            { key: "outstanding_amount", label: "Outstanding Amount", value: record?.outstanding_amount, forceCurrency: true },
            { key: "advance_percentage", label: "Advance %", value: isPresent(record?.advance_percentage) ? formatPercentValue(record.advance_percentage) : undefined },
          ].filter((row) => isPresent(row.value))}
          currency={currency}
        />

        <div style={{ display: "grid", gap: 7, padding: "10px 12px", borderRadius: 12, background: "rgba(79,124,255,0.07)", border: "1px solid rgba(79,124,255,0.14)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
            <span style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 13 }}>Payment Coverage</span>
            <strong style={{ color: T.brand, fontFamily: T.fontDisplay, fontSize: 13 }}>{formatPercentValue(coverage)}</strong>
          </div>
          <ProgressBar value={coverage} color={T.brand} />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 10,
            minWidth: 0,
          }}
        >
          <div style={{ display: "grid", gap: 6, padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: 12, background: "#FFFFFF", minWidth: 0 }}>
            <div style={{ color: T.muted, fontFamily: T.fontDisplay, fontSize: 13 }}>Payment Modes</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
              {paymentModes.length ? paymentModes.map((mode, index) => <StatusBadge key={`${mode}-${index}`} value={mode} />) : <span style={tooltipTextStyle}>{DASH}</span>}
            </div>
          </div>

          <div style={{ display: "grid", gap: 6, padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: 12, background: "#FFFFFF", minWidth: 0 }}>
            <div style={{ color: T.muted, fontFamily: T.fontDisplay, fontSize: 13 }}>Transaction Dates</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
              {transactionDates.length ? transactionDates.map((date, index) => <StatusBadge key={`${date}-${index}`} value={formatDateValue(date)} />) : <span style={tooltipTextStyle}>{DASH}</span>}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={() => onViewDetails(record)}
            style={{
              border: "1px solid rgba(79,124,255,0.24)",
              borderRadius: 10,
              background: T.brand,
              color: "#fff",
              padding: "8px 13px",
              fontFamily: T.fontSemiBold,
              fontSize: 13,
              cursor: "pointer",
              boxShadow: "0px 10px 20px rgba(79,124,255,0.22)",
            }}
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  );
};

const PaymentAdviceRecordsTable = ({ records = [], currency, onViewDetails }) => (
  <div style={{ overflowX: "auto", border: "1px solid rgba(79,124,255,0.18)", borderRadius: 18, background: "linear-gradient(145deg, #FFFFFF 0%, #F8FBFF 100%)", boxShadow: "0 22px 48px rgba(15,23,42,0.1)" }}>
    <table style={{ width: "100%", minWidth: 1400, borderCollapse: "collapse" }}>
      <thead>
        <tr style={{ background: "linear-gradient(90deg, rgba(79,124,255,0.16), rgba(36,200,165,0.12), rgba(245,165,36,0.1))" }}>
          {["Payment Advice No", "PO Number", "Invoice No", "Payment Status", "Payment Mode", "Reference No", "Transaction Date", "Invoice Value", "Paid Amount", "Outstanding", "Coverage", "Action"].map((label, index, headings) => (
            <th
              key={label}
              style={{
                padding: "14px 16px",
                color: T.text,
                fontFamily: T.fontDisplay,
                fontSize: 14,
                textAlign: label === "Action" ? "center" : "left",
                borderBottom: `1px solid ${T.border}`,
                whiteSpace: "nowrap",
                ...(index === 0 ? paymentTableStickyFirstHeaderStyle : {}),
                ...(index === headings.length - 1 ? paymentTableStickyLastHeaderStyle : {}),
              }}
            >
              {label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {records.map((record, index) => {
          const resultJson = parseJsonSafely(record?.result_json);
          const paymentAdviceMatching = resultJson?.payment_advice_matching || {};
          const paymentAdvice = asArray(resultJson?.payment_advice_data)[0] || asArray(record?.payment_advice_data)[0] || asArray(paymentAdviceMatching?.payment_advice_data)[0] || paymentAdviceMatching?.payment_advice || paymentAdviceMatching?.payment_advice_data || record?.payment_advice_data || {};
          const invoiceData = asArray(resultJson?.invoice_data)[0] || resultJson?.payment_advice_matching?.invoice || resultJson?.payment_advice_matching?.invoice_data || {};
          const paymentSummary = record?.payment_advance_summary || record?.payment_advice_summary || {};
          const coverage = getFirstDefinedValue(record?.payment_coverage_percent, paymentSummary?.payment_coverage_percent, paymentSummary?.coverage_percent, 0);
          const paymentStatus = getFirstDefinedValue(record?.payment_status, paymentSummary?.payment_status, record?.status);
          const statusTone = getRecordStatusTone(paymentStatus);
          const paymentAdviceNo = getFirstDefinedValue(record?.payment_advice_number, record?.payment_advice_no, paymentAdvice?.payment_advice_number, paymentAdvice?.payment_advice_no, paymentAdvice?.advice_number, paymentAdvice?.advice_no, record?.match_code);
          const invoiceNo = getFirstDefinedValue(record?.invoice_number, record?.invoice_no, invoiceData?.invoice_number, invoiceData?.invoice_no, invoiceData?.document_no, paymentAdvice?.invoice_number, paymentAdvice?.invoice_no);
          const paymentMode = getFirstDefinedValue(record?.payment_mode, paymentAdvice?.payment_mode, paymentAdvice?.mode, paymentAdvice?.mode_of_payment, paymentAdvice?.payment_product, paymentAdvice?.payment_details_1, paymentAdviceMatching?.payment_mode, paymentAdviceMatching?.mode);
          const referenceNo = getFirstDefinedValue(record?.reference_no, record?.transaction_reference, record?.utr, record?.rrn, record?.utr_rrn_no, paymentAdvice?.utr_rrn_no, paymentAdvice?.utr_rrn, paymentAdvice?.utr, paymentAdvice?.rrn, paymentAdvice?.reference, paymentAdvice?.reference_no, paymentAdvice?.transaction_reference, paymentAdviceMatching?.utr_rrn_no, paymentAdviceMatching?.utr, paymentAdviceMatching?.rrn, paymentAdviceMatching?.reference, paymentAdviceMatching?.reference_no, paymentAdviceMatching?.transaction_reference);
          const transactionDate = getFirstDefinedValue(record?.transaction_date, record?.payment_date, paymentAdvice?.transaction_date, paymentAdvice?.payment_date, paymentAdvice?.date);
          const rowBackground = index % 2 === 0 ? "#FFFFFF" : "#F8FBFF";
          return (
            <tr key={`${record?.token || record?.match_code || "record"}-${index}`} style={{ background: rowBackground }}>
              <td style={{ ...paymentTableCellStyle, ...paymentTableStickyFirstCellStyle, background: rowBackground }}><strong style={{ color: T.brand, fontFamily: T.fontDisplay }}>{displayValue(paymentAdviceNo)}</strong></td>
              <td style={paymentTableCellStyle}>{displayValue(getFirstDefinedValue(record?.purchase_order_no, record?.po_number, paymentAdvice?.purchase_order_no, paymentAdvice?.po_number, invoiceData?.purchase_order_no, invoiceData?.po_number))}</td>
              <td style={paymentTableCellStyle}>{displayValue(invoiceNo)}</td>
              <td style={paymentTableCellStyle}><span style={{ display: "inline-flex", borderRadius: 999, border: `1px solid ${statusTone.border}`, background: statusTone.bg, color: statusTone.color, padding: "7px 11px", fontFamily: T.fontSemiBold, fontSize: 14 }}>{displayValue(paymentStatus)}</span></td>
              <td style={paymentTableCellStyle}>{displayValue(paymentMode)}</td>
              <td style={paymentTableCellStyle}>{displayValue(referenceNo)}</td>
              <td style={paymentTableCellStyle}>{formatDateValue(transactionDate)}</td>
              <td style={paymentTableCellStyle}>{formatPreciseCurrency(record?.invoice_total_amount || paymentSummary?.invoice_total_amount, currency)}</td>
              <td style={paymentTableCellStyle}>{formatPreciseCurrency(record?.paid_amount || paymentSummary?.paid_amount, currency)}</td>
              <td style={paymentTableCellStyle}>{formatPreciseCurrency(record?.outstanding_amount || paymentSummary?.outstanding_amount, currency)}</td>
              <td style={paymentTableCellStyle}>
                <div style={{ display: "grid", gap: 6, minWidth: 120 }}>
                  <span style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 14 }}>{formatPercentValue(coverage)}</span>
                  <ProgressBar value={coverage} color={T.brand} />
                </div>
              </td>
              <td style={{ ...paymentTableCellStyle, ...paymentTableStickyLastCellStyle, background: rowBackground, textAlign: "center" }}>
                <button type="button" onClick={() => onViewDetails(record)} style={{ border: "1px solid rgba(79,124,255,0.28)", borderRadius: 10, background: "linear-gradient(135deg, #4F7CFF, #14B8A6)", color: "#fff", padding: "8px 12px", fontFamily: T.fontSemiBold, fontSize: 14, cursor: "pointer", whiteSpace: "nowrap" }}>
                  View Details
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const PaymentTrendPanel = ({ title, note, monthlyRows = [], yearlyRows = [], currency }) => {
  const [mode, setMode] = useState("monthly");
  const data = mode === "monthly" ? monthlyRows : yearlyRows;

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <ChartTitle title={title} note={note} />
        <div
          style={{
            display: "inline-flex",
            border: `1px solid ${T.border}`,
            borderRadius: 12,
            background: "#fff",
            padding: 4,
            gap: 4,
          }}
        >
          {["monthly", "yearly"].map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => setMode(option)}
              style={{
                border: "none",
                borderRadius: 9,
                background: mode === option ? "rgba(79,124,255,0.12)" : "transparent",
                color: mode === option ? T.brand : T.textSec,
                padding: "8px 12px",
                fontFamily: T.fontSemiBold,
                fontSize: 13,
                textTransform: "capitalize",
                cursor: "pointer",
              }}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
      {data.length ? (
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 36 }}>
              <CartesianGrid stroke="rgba(226,232,240,0.82)" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={56} />
              <YAxis yAxisId="left" tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} width={54} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} width={70} tickFormatter={(value) => formatCurrency(value, currency)} />
              <RechartsLegend wrapperStyle={{ fontFamily: T.fontSemiBold, fontSize: 13, color: T.textSec }} />
              <RechartsTooltip
                content={(props) => (
                  <CustomTooltip
                    {...props}
                    formatter={(value, item) =>
                      item?.dataKey === "paidAmount" ? formatPreciseCurrency(value, currency) : formatNumber(value)
                    }
                  />
                )}
              />
              <Line yAxisId="left" type="monotone" dataKey="recordCount" name="Record Count" stroke={T.brand} strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
              <Line yAxisId="left" type="monotone" dataKey="documentCount" name="Document Count" stroke={T.info} strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
              <Line yAxisId="right" type="monotone" dataKey="paidAmount" name="Paid Amount" stroke={T.success} strokeWidth={3} dot={{ r: 4, strokeWidth: 2 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart label={`No ${mode} payment advance data available`} />
      )}
    </div>
  );
};

const ReconciliationDetailsDrawer = ({ open, onClose, record, currency }) => {
  const resultJson = parseJsonSafely(record?.result_json);
  const paymentSummary =
    record?.payment_advance_summary ||
    record?.payment_advice_summary ||
    resultJson?.payment_advance_summary ||
    resultJson?.payment_advice_summary ||
    {};
  const matchingDetails =
    record?.matching_details ||
    resultJson?.matching_details ||
    {};
  const paymentAdviceData = asArray(resultJson?.payment_advice_data);
  const primaryPaymentAdvice = paymentAdviceData[0] || {};
  const detailPaymentAdviceNo = getFirstDefinedValue(record?.payment_advice_number, record?.payment_advice_no, primaryPaymentAdvice?.payment_advice_number, primaryPaymentAdvice?.payment_advice_no, primaryPaymentAdvice?.advice_no, record?.match_code);
  const documentFiles = Array.isArray(resultJson?.files)
    ? resultJson.files
    : Array.isArray(record?.files)
      ? record.files
      : [];

  if (!open || !record) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,23,42,0.42)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        pointerEvents: "auto",
        padding: 8,
        boxSizing: "border-box",
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{
          width: "min(960px, 100%)",
          maxWidth: "100%",
          maxHeight: "calc(100dvh - 24px)",
          overflowY: "auto",
          overflowX: "hidden",
          background: "#F8FAFC",
          boxShadow: "0px 24px 70px rgba(15,23,42,0.24)",
          padding: "16px",
          display: "grid",
          gap: 16,
          alignContent: "start",
          borderRadius: 16,
          minWidth: 0,
          pointerEvents: "auto",
          boxSizing: "border-box",
          overscrollBehavior: "contain",
        }}
      >
        <div
          style={{
            position: "sticky",
            top: -16,
            zIndex: 2,
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            alignItems: "flex-start",
            background: "#F8FAFC",
            padding: "4px 0 12px",
            minWidth: 0,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 22 }}>
              {displayValue(record?.match_code)}
            </div>
            <div style={{ ...tooltipTextStyle, marginTop: 4 }}>
              Payment, reconciliation, and extracted document details
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              border: `1px solid ${T.border}`,
              borderRadius: 5,
              background: "#d13535",
              color: "#ffff",
              padding: "9px 12px",
              fontFamily: T.fontSemiBold,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>

        <DetailAccordion title="Overview" subtitle="Core reconciliation metadata">
          <KeyValueGrid
            rows={[
              
              { key: "payment_advice_no", label: "Payment Advice No", value: detailPaymentAdviceNo },
              { key: "purchase_order_no", label: "Purchase Order No", value: record?.purchase_order_no },
              { key: "vendor_name", label: "Vendor Name", value: record?.vendor_name },
              { key: "status", label: "Status", value: record?.status },
              { key: "matching_mode", label: "Matching Mode", value: record?.matching_mode || resultJson?.matching_mode },
              { key: "created_at", label: "Created At", value: formatDateValue(record?.created_at) },
              { key: "last_updated_at", label: "Last Updated At", value: formatDateValue(record?.last_updated_at) },
            ]}
            currency={currency}
          />
        </DetailAccordion>

        <DetailAccordion title="Payment Summary" subtitle="Important payment advance summary">
          <KeyValueGrid
            rows={getImportantPaymentSummaryRows(paymentSummary)}
            currency={currency}
          />
        </DetailAccordion>

        <DetailAccordion title="Matching Details" subtitle="Backend status, mismatch reasons, and compared items">
          <div style={{ display: "grid", gap: 14 }}>
            <KeyValueGrid
              rows={[
                { key: "matching_type", label: "Matching Type", value: matchingDetails?.matching_type },
                { key: "overall_status", label: "Overall Status", value: matchingDetails?.overall_status },
              ].filter((row) => isPresent(row.value))}
              currency={currency}
            />
            {matchingDetails?.status_summary && typeof matchingDetails.status_summary === "object" ? (
              <KeyValueGrid
                rows={getObjectEntries(matchingDetails.status_summary).map(([key, value]) => ({
                  key,
                  label: normalizeLabel(key),
                  value,
                }))}
                currency={currency}
              />
            ) : null}
            {asArray(matchingDetails?.mismatch_reasons).length ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {asArray(matchingDetails.mismatch_reasons).map((reason, index) => (
                  <StatusBadge key={`${reason}-${index}`} value={reason} />
                ))}
              </div>
            ) : null}
            {asArray(matchingDetails?.item_comparisons).length ? (
              <div style={{ display: "grid", gap: 12 }}>
                {asArray(matchingDetails.item_comparisons).map((item, index) => (
                  <ItemComparisonCard key={`comparison-${index}`} item={item} currency={currency} />
                ))}
              </div>
            ) : null}
          </div>
        </DetailAccordion>

        <DetailAccordion title="Payment Advice" subtitle="All available payment advice fields">
          {paymentAdviceData.length ? (
            <div style={{ display: "grid", gap: 12, minWidth: 0 }}>
              {paymentAdviceData.map((item, index) => (
                <div
                  key={`payment-advice-${index}`}
                  style={{
                    border: `1px solid ${T.border}`,
                    borderRadius: 14,
                    background: "#fff",
                    padding: 14,
                  }}
                >
                  <KeyValueGrid
                    rows={getImportantPaymentAdviceRows(item)}
                    currency={currency}
                  />
                </div>
              ))}
            </div>
          ) : (
            <EmptyChart label="No payment advice data available" />
          )}
        </DetailAccordion>

        {resultJson?.payment_advice_matching ? (
          <DetailAccordion title="Payment Advice Matching" subtitle="Backend payment advice comparison data">
            <div style={{ display: "grid", gap: 14, overflowX: "auto" }}>
              {[
                {
                  title: "Invoice",
                  source:
                    resultJson.payment_advice_matching?.invoice ||
                    resultJson.payment_advice_matching?.invoice_data ||
                    resultJson.payment_advice_matching,
                  rows: [
                    ["status", "Status"],
                    ["advance_paid", "Advance Paid", true],
                    ["invoice_total", "Invoice Total", true],
                    ["invoice_balance", "Invoice Balance", true],
                    ["expected_balance", "Expected Balance", true],
                  ],
                },
                {
                  title: "PO Advance",
                  source:
                    resultJson.payment_advice_matching?.po_advance ||
                    resultJson.payment_advice_matching?.po_advance_data ||
                    resultJson.payment_advice_matching,
                  rows: [
                    ["po_amount", "PO Amount", true],
                    ["advance_percentage", "Advance Percentage"],
                    ["expected_advance_amount", "Expected Advance Amount", true],
                  ],
                },
                {
                  title: "Payment Advice",
                  source:
                    resultJson.payment_advice_matching?.payment_advice ||
                    resultJson.payment_advice_matching?.payment_advice_data ||
                    resultJson.payment_advice_matching,
                  rows: [
                    ["status", "Status"],
                    ["paid_amount", "Paid Amount", true],
                  ],
                },
              ].map((section) => (
                <div key={section.title}>
                  <div style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 15, marginBottom: 8 }}>
                    {section.title}
                  </div>
                  <div style={{ overflowX: "auto", maxWidth: "100%", border: `1px solid ${T.border}`, borderRadius: 12 }}>
                    <table style={{ width: "100%", minWidth: 680, borderCollapse: "collapse", background: "#fff" }}>
                      <thead>
                        <tr style={{ background: "linear-gradient(90deg, rgba(79,124,255,0.14), rgba(14,165,233,0.1))" }}>
                          {section.rows.map(([, label]) => (
                            <th key={label} style={{ padding: "12px", color: T.text, fontFamily: T.fontDisplay, fontSize: 13, textAlign: "left", borderBottom: `1px solid ${T.border}` }}>
                              {label}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          {section.rows.map(([key, label, forceCurrency]) => (
                            <td key={key} style={{ padding: "12px", color: T.textSec, fontFamily: T.fontBody, fontSize: 13, borderBottom: `1px solid ${T.border}` }}>
                              {displayValue(section.source?.[key], { currency, forceCurrency })}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </DetailAccordion>
        ) : null}

        <ResultJsonSection title="PO Data" records={resultJson?.po_data} currency={currency} docPageCount={resultJson?.doc_page_count} />
        <ResultJsonSection title="Invoice Data" records={resultJson?.invoice_data} currency={currency} docPageCount={resultJson?.doc_page_count} />
        <ResultJsonSection title="GRN Data" records={resultJson?.grn_data} currency={currency} docPageCount={resultJson?.doc_page_count} />

        {documentFiles.length ? (
          <DetailAccordion title="Documents" subtitle="Files returned in result_json.files">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", gap: 12, minWidth: 0 }}>
              {documentFiles.map((item, index) => {
                const fileRecord = typeof item === "string" ? { file: item } : item || {};
                return (
                  <DocumentCard
                    key={`file-${index}`}
                    file={fileRecord?.file || fileRecord?.file_name || item}
                    docType={fileRecord?.doc_type}
                    status={fileRecord?.status}
                    docPageCount={fileRecord?.doc_page_count ?? resultJson?.doc_page_count}
                  />
                );
              })}
            </div>
          </DetailAccordion>
        ) : null}

      </div>
    </div>
  );
};

const tooltipTextStyle = {
  margin: 0,
  color: T.muted,
  fontFamily: T.fontBody,
  fontSize: 14,
  lineHeight: 1.45,
};

const paymentTableCellStyle = {
  padding: "13px 16px",
  color: T.textSec,
  fontFamily: T.fontSemiBold,
  fontSize: 14,
  borderBottom: `1px solid ${T.border}`,
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

const paymentTableStickyFirstHeaderStyle = {
  position: "sticky",
  left: 0,
  zIndex: 5,
  minWidth: 180,
  width: 180,
  background: "#EAF1F7",
  boxShadow: "2px 0 0 0 #E2E8F0 inset",
};

const paymentTableStickyLastHeaderStyle = {
  position: "sticky",
  right: 0,
  zIndex: 6,
  minWidth: 128,
  width: 128,
  background: "#EEF3EC",
  boxShadow: "-2px 0 0 0 #E2E8F0 inset",
};

const paymentTableStickyFirstCellStyle = {
  position: "sticky",
  left: 0,
  zIndex: 3,
  minWidth: 180,
  width: 180,
  boxShadow: "2px 0 0 0 #E2E8F0 inset",
};

const paymentTableStickyLastCellStyle = {
  position: "sticky",
  right: 0,
  zIndex: 4,
  minWidth: 128,
  width: 128,
  boxShadow: "-2px 0 0 0 #E2E8F0 inset",
};

const selectStyle = {
  height: 34,
  border: `1px solid ${T.border}`,
  borderRadius: 10,
  background: "#fff",
  color: T.textSec,
  fontFamily: T.fontSemiBold,
  fontSize: 14,
  padding: "0 10px",
  outline: "none",
};

const metricGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 16,
  marginBottom: 20,
  alignItems: "stretch",
};

const wideSplitGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: 20,
  alignItems: "stretch",
};

const chartShellStyle = {
  minWidth: 0,
  border: "1px solid rgba(226,232,240,0.7)",
  borderRadius: 16,
  padding: 16,
  background: "linear-gradient(180deg, #FFFFFF 0%, #F9FBFF 100%)",
};

const SectionCard = ({ title, subtitle, children }) => (
  <section
    style={{
      background: "linear-gradient(180deg, #FFFFFF 0%, #FBFDFF 100%)",
      border: `1px solid ${T.border}`,
      borderRadius: 18,
      boxShadow: "0px 14px 34px rgba(15,23,42,0.07)",
      padding: "24px",
      marginBottom: 22,
      minWidth: 0,
      overflow: "visible",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 16,
        marginBottom: 20,
        paddingBottom: 14,
        borderBottom: "1px solid rgba(148,163,184,0.18)",
      }}
    >
      <div>
        <h2 style={{ margin: 0, fontSize: 16, fontFamily: T.fontDisplay, color: T.text, letterSpacing: 0 }}>
          {title}
        </h2>
        {subtitle ? <p style={{ ...tooltipTextStyle, marginTop: 4 }}>{subtitle}</p> : null}
      </div>
    </div>
    {children}
  </section>
);

const ChartTitle = ({ title, note }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 16, fontFamily: T.fontDisplay, color: T.text }}>{title}</div>
    {note ? <div style={{ ...tooltipTextStyle, marginTop: 3 }}>{note}</div> : null}
  </div>
);

const TrendControls = ({ data = [], selectedYear, selectedMonth, onYearChange, onMonthChange }) => {
  const years = useMemo(
    () => [...new Set(data.map((row) => row.year).filter(Boolean))].sort((a, b) => a - b),
    [data],
  );
  const months = useMemo(() => {
    const monthRows = data.filter((row) => !selectedYear || row.year === selectedYear);
    return [...new Map(monthRows.map((row) => [row.month, row.monthName || row.label])).entries()]
      .filter(([month]) => month)
      .sort(([left], [right]) => left - right);
  }, [data, selectedYear]);

  useEffect(() => {
    if (!years.length || years.includes(selectedYear)) return;
    onYearChange(years[years.length - 1]);
  }, [onYearChange, selectedYear, years]);

  useEffect(() => {
    if (selectedMonth === ALL_MONTHS) return;
    if (months.some(([month]) => month === selectedMonth)) return;
    onMonthChange(ALL_MONTHS);
  }, [months, onMonthChange, selectedMonth]);

  if (!years.length) return null;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 8,
        marginBottom: 10,
        flexWrap: "wrap",
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <select
        value={selectedYear || years[years.length - 1]}
        onChange={(event) => onYearChange(toNumber(event.target.value))}
        style={selectStyle}
      >
        {years.map((year) => (
          <option key={year} value={year}>
            {year}
          </option>
        ))}
      </select>
      <select
        value={selectedMonth}
        onChange={(event) =>
          onMonthChange(event.target.value === ALL_MONTHS ? ALL_MONTHS : toNumber(event.target.value))
        }
        style={selectStyle}
      >
        <option value={ALL_MONTHS}>All Months</option>
        {months.map(([month, label]) => (
          <option key={month} value={month}>
            {label}
          </option>
        ))}
      </select>
    </div>
  );
};

const useTrendRows = (data = [], enabled = false) => {
  const years = useMemo(
    () => [...new Set(data.map((row) => row.year).filter(Boolean))].sort((a, b) => a - b),
    [data],
  );
  const [selectedYear, setSelectedYear] = useState(years[years.length - 1] || "");
  const [selectedMonth, setSelectedMonth] = useState(ALL_MONTHS);

  useEffect(() => {
    if (!years.length) {
      setSelectedYear("");
      setSelectedMonth(ALL_MONTHS);
      return;
    }
    setSelectedYear((current) => (years.includes(current) ? current : years[years.length - 1]));
  }, [years]);

  const filteredRows = useMemo(() => {
    if (!enabled || !selectedYear) return data;
    return data.filter((row) => {
      if (row.year !== selectedYear) return false;
      return selectedMonth === ALL_MONTHS || row.month === selectedMonth;
    });
  }, [data, enabled, selectedMonth, selectedYear]);

  return {
    filteredRows,
    selectedYear,
    selectedMonth,
    setSelectedYear,
    setSelectedMonth,
  };
};

const EmptyChart = ({ label = "No dashboard data available" }) => (
  <div
    style={{
      height: 260,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: T.muted,
      fontFamily: T.fontBody,
      fontSize: 14,
      border: "1px dashed rgba(148,163,184,0.35)",
      borderRadius: 16,
      background: "#F8FAFC",
    }}
  >
    {label}
  </div>
);

const CustomTooltip = ({ active, payload, label, formatter = formatNumber }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid rgba(226,232,240,0.92)",
        boxShadow: "0px 12px 30px rgba(15,23,42,0.12)",
        borderRadius: 12,
        padding: "10px 12px",
        minWidth: 170,
        maxWidth: 320,
      }}
    >
      <div style={{ color: T.muted, fontFamily: T.fontSemiBold, fontSize: 14, marginBottom: 6 }}>
        {label}
      </div>
      {payload.map((item) => (
        <div
          key={`${item.name}-${item.dataKey}`}
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            color: T.text,
            fontFamily: T.fontBody,
            fontSize: 14,
            marginTop: 4,
          }}
        >
          <span style={{ color: item.color || T.textSec }}>{item.name}</span>
          <strong style={{ fontFamily: T.fontDisplay }}>{formatter(item.value, item)}</strong>
        </div>
      ))}
    </div>
  );
};

const Legend = ({ rows = [], colors = STATUS_COLORS, valueKey = "value", formatter = formatNumber }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "10px 14px", marginTop: 14 }}>
    {rows.map((row, index) => (
      <div key={row.name || row.label} style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
        <span
          style={{
            width: 9,
            height: 9,
            borderRadius: 999,
            background: colors[index % colors.length],
          }}
        />
        <span style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 14 }}>
          {row.name || row.label}: {formatter(row[valueKey])}
        </span>
      </div>
    ))}
  </div>
);

const DefaultMetricTooltip = ({ title, value, note }) => (
  <div style={{ display: "grid", gap: 9 }}>
    <div>
      <div style={{ color: T.muted, fontFamily: T.fontDisplay, fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {title}
      </div>
      <div style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 15, marginTop: 2 }}>
        {value} total
      </div>
      <div style={{ color: T.muted, fontFamily: T.fontBody, fontSize: 12, marginTop: 1 }}>
        Year-wise breakdown
      </div>
    </div>
    <div style={{ display: "grid", gap: 6 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 20,
          padding: "7px 9px",
          borderRadius: 10,
          background: "#F8FAFC",
        }}
      >
        <span style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 13 }}>All years</span>
        <span style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 13, whiteSpace: "nowrap" }}>{value}</span>
      </div>
      {note ? <div style={{ color: T.muted, fontFamily: T.fontBody, fontSize: 12 }}>{note}</div> : null}
    </div>
  </div>
);

const MetricCard = ({ title, value, note, accent = T.brand, tooltip, onClick, emphasis = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipContent = tooltip || <DefaultMetricTooltip title={title} value={value} note={note} />;
  const interactive = Boolean(onClick || tooltipContent);

  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      onFocus={() => setIsOpen(true)}
      onBlur={() => setIsOpen(false)}
      onClick={onClick}
      onKeyDown={(event) => {
        if (!onClick || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        onClick();
      }}
      style={{
        background: `linear-gradient(180deg, ${emphasis ? "rgba(20,184,166,0.08)" : "#FFFFFF"} 0%, #FFFFFF 72%)`,
        border: `1px solid ${emphasis ? "rgba(20,184,166,0.28)" : T.border}`,
        borderRadius: 16,
        minHeight: emphasis ? 142 : 118,
        padding: "20px 20px 18px",
        boxShadow: emphasis ? "0px 18px 42px rgba(15,23,42,0.12)" : "0px 10px 28px rgba(15,23,42,0.065)",
        position: "relative",
        overflow: "visible",
        cursor: interactive ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: "0 0 auto",
          height: 4,
          background: `linear-gradient(90deg, ${accent}, rgba(255,255,255,0.6))`,
          borderRadius: "16px 16px 0 0",
        }}
      />
      <div>
        <div style={{ color: T.muted, fontFamily: T.fontDisplay, fontSize: 16, marginBottom: 10 }}>
          {title}
        </div>
        <div
          style={{
            color: accent,
            fontFamily: T.fontDisplay,
            fontSize: emphasis ? 27 : 23,
            lineHeight: 1.15,
            wordBreak: "break-word",
          }}
        >
          {value}
        </div>
      </div>
      {note ? (
        <div style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 14, marginTop: 8 }}>
          {note}
        </div>
      ) : null}
      {tooltipContent && isOpen ? (
        <div
          style={{
            position: "absolute",
            zIndex: 20,
            left: 14,
            right: 14,
            top: "calc(100% + 8px)",
            background: "#fff",
            border: `1px solid ${T.border}`,
            boxShadow: "0px 16px 36px rgba(15,23,42,0.15)",
            borderRadius: 12,
            padding: "10px 12px",
          }}
        >
          {tooltipContent}
        </div>
      ) : null}
    </div>
  );
};

const BarPanel = ({ title, note, data, bars, height = 280, formatter = formatNumber, onClick, trendFilter = false }) => {
  const {
    filteredRows,
    selectedYear,
    selectedMonth,
    setSelectedYear,
    setSelectedMonth,
  } = useTrendRows(data, trendFilter);

  return (
    <div style={{ minWidth: 0, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
      <ChartTitle title={title} note={note} />
      {trendFilter ? (
        <TrendControls
          data={data}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          onYearChange={setSelectedYear}
          onMonthChange={setSelectedMonth}
        />
      ) : null}
      {filteredRows.length ? (
        <div style={{ width: "100%", height }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={filteredRows} margin={{ top: 10, right: 14, left: 0, bottom: 36 }}>
              <CartesianGrid stroke="rgba(226,232,240,0.82)" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={56} />
              <YAxis tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} width={54} tickFormatter={formatter} />
              <RechartsTooltip content={(props) => <CustomTooltip {...props} formatter={formatter} />} />
              {bars.map((bar) => (
                <Bar key={bar.key} dataKey={bar.key} name={bar.name} fill={bar.color} radius={[8, 8, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart />
      )}
    </div>
  );
};

const LinePanel = ({ title, note, data, lines, height = 280, formatter = formatNumber, onClick, trendFilter = false }) => {
  const {
    filteredRows,
    selectedYear,
    selectedMonth,
    setSelectedYear,
    setSelectedMonth,
  } = useTrendRows(data, trendFilter);

  return (
    <div style={{ minWidth: 0, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
      <ChartTitle title={title} note={note} />
      {trendFilter ? (
        <TrendControls
          data={data}
          selectedYear={selectedYear}
          selectedMonth={selectedMonth}
          onYearChange={setSelectedYear}
          onMonthChange={setSelectedMonth}
        />
      ) : null}
      {filteredRows.length ? (
        <div style={{ width: "100%", height }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={filteredRows} margin={{ top: 10, right: 16, left: 0, bottom: 36 }}>
              <CartesianGrid stroke="rgba(226,232,240,0.82)" strokeDasharray="3 4" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} interval={0} angle={-20} textAnchor="end" height={56} />
              <YAxis tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} width={54} tickFormatter={formatter} />
              <RechartsTooltip content={(props) => <CustomTooltip {...props} formatter={formatter} />} />
              {lines.map((line) => (
                <Line key={line.key} type="monotone" dataKey={line.key} name={line.name} stroke={line.color} strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <EmptyChart />
      )}
    </div>
  );
};

const DonutPanel = ({ title, note, data, height = 280, formatter = formatNumber, onClick }) => {
  const total = data.reduce((sum, row) => sum + toNumber(row.value), 0);

  return (
    <div style={{ minWidth: 0, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
      <ChartTitle title={title} note={note} />
      {data.length ? (
        <>
          <div style={{ width: "100%", height }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="82%" paddingAngle={3}>
                  {data.map((entry, index) => (
                    <Cell key={entry.name} fill={STATUS_COLORS[index % STATUS_COLORS.length]} />
                  ))}
                </Pie>
                <RechartsTooltip
                  content={(props) => (
                    <CustomTooltip
                      {...props}
                      formatter={(value) => `${formatter(value)} (${toPercent(value, total)})`}
                    />
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <Legend rows={data} formatter={formatter} />
        </>
      ) : (
        <EmptyChart />
      )}
    </div>
  );
};

const HorizontalBarPanel = ({ title, note, data, valueKey = "value", height = 320, formatter = formatNumber, onClick }) => (
  <div style={{ minWidth: 0, cursor: onClick ? "pointer" : "default" }} onClick={onClick}>
    <ChartTitle title={title} note={note} />
    {data.length ? (
      <div style={{ width: "100%", height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 6, right: 20, left: 20, bottom: 6 }}>
            <CartesianGrid stroke="rgba(226,232,240,0.82)" strokeDasharray="3 4" horizontal={false} />
            <XAxis type="number" tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} tickFormatter={formatter} />
            <YAxis type="category" dataKey="label" width={132} tick={{ fill: T.muted, fontSize: 14, fontFamily: T.fontSemiBold }} tickLine={false} axisLine={false} />
            <RechartsTooltip content={(props) => <CustomTooltip {...props} formatter={formatter} />} />
            <Bar dataKey={valueKey} name={title} fill={T.purple} radius={[0, 8, 8, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    ) : (
      <EmptyChart />
    )}
  </div>
);

const InsightList = ({ rows = [], valueKey = "value", formatter = formatNumber }) => (
  <div style={{ display: "grid", gap: 10 }}>
    {rows.length ? rows.map((row, index) => (
      <div
        key={`${row.label}-${index}`}
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          alignItems: "center",
          padding: "11px 12px",
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          background: "#fff",
        }}
      >
        <span style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 14, minWidth: 0 }}>
          {row.label}
        </span>
        <strong style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 14, whiteSpace: "nowrap" }}>
          {formatter(row[valueKey])}
        </strong>
      </div>
    )) : <EmptyChart label="No records available" />}
  </div>
);

const ControlTowerBanner = ({ currency, bidSummary, poSummary, invoiceSummary }) => {
  const selectedBidValue = toNumber(bidSummary?.selected_bid_value);
  const poBudget = toNumber(poSummary?.total_po_budget);
  const invoiceAmount = toNumber(invoiceSummary?.total_invoice_amount);
  const totalCommercialValue = selectedBidValue + poBudget + invoiceAmount;
  const healthRows = [
    { label: "Bid Win Rate", value: toPercent(bidSummary?.success_bid, bidSummary?.submitted_bid), color: T.success },
    { label: "Invoice Verified", value: toPercent(invoiceSummary?.verified_count, invoiceSummary?.total_invoice_count), color: T.info },
  ];

  return (
    <section
      style={{
        background: "linear-gradient(135deg, #FFFFFF 0%, #F2F7FF 58%, #ECFDF9 100%)",
        border: `1px solid ${T.border}`,
        borderRadius: 20,
        boxShadow: "0px 16px 40px rgba(15,23,42,0.08)",
        padding: "24px",
        marginBottom: 20,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: 20,
        alignItems: "center",
      }}
    >
      <div>
        <div
          style={{
            display: "inline-flex",
            padding: "5px 10px",
            borderRadius: 999,
            background: "rgba(79,124,255,0.1)",
            color: T.brand,
            fontFamily: T.fontDisplay,
            fontSize: 14,
            marginBottom: 12,
          }}
        >
          Supplier Control Tower
        </div>
        <h1
          style={{
            margin: 0,
            color: T.text,
            fontFamily: T.fontDisplay,
            fontSize: 26,
            lineHeight: 1.2,
            letterSpacing: 0,
          }}
        >
          
          Status of Bid Submissions, POs, Invoice Submissions, and Customer Payments.
        </h1>
       
      </div>
      <div
        style={{
          background: "rgba(255,255,255,0.78)",
          border: "1px solid rgba(226,232,240,0.78)",
          borderRadius: 16,
          padding: 16,
          display: "grid",
          gap: 13,
        }}
      >
        <div>
          <div style={{ color: T.muted, fontFamily: T.fontDisplay, fontSize: 14 }}>Commercial Value Tracked</div>
          <div style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 24, marginTop: 3 }}>
            {formatCurrency(totalCommercialValue, currency)}
          </div>
        </div>
        {healthRows.map((row) => (
          <div key={row.label}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
              <span style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 14 }}>{row.label}</span>
              <strong style={{ color: row.color, fontFamily: T.fontDisplay, fontSize: 14 }}>{row.value}</strong>
            </div>
            <div style={{ height: 7, borderRadius: 999, background: "rgba(226,232,240,0.9)", overflow: "hidden" }}>
              <div
                style={{
                  width: row.value,
                  maxWidth: "100%",
                  height: "100%",
                  borderRadius: 999,
                  background: row.color,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const VendorAdminDashboardTab = () => {
  const data = useSelector((state) => state.loginData.value);
  const navigate = useNavigate();

  const authToken = data?.auth_token;
  const vendorToken = data?.user?.token;

  const [, setLoadingState] = useState(false);
  const [dashboardData, setDashboardData] = useState({});
  const [vendorUserCount, setVendorUserCount] = useState(0);
  const [selectedFourWayRecord, setSelectedFourWayRecord] = useState(null);
  const [paymentAdvicePage, setPaymentAdvicePage] = useState(1);
  const [activeVendorModule, setActiveVendorModule] = useState("bid");
  const paymentAdviceRecordsRef = useRef(null);
  const scrollToPaymentAdviceRecords = useCallback(() => {
    paymentAdviceRecordsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const postWithAuth = useCallback(
    (url, payload) =>
      axios({
        method: "POST",
        url,
        data: payload,
        headers: { Authorization: `Bearer ${authToken}` },
      }),
    [authToken],
  );

  const getAnalyticsValues = useCallback(async () => {
    if (!authToken || !vendorToken) return;

    const formData = new FormData();
    formData.append("vendor_token", vendorToken);

    setLoadingState(true);
    try {
      const response = await postWithAuth(API_ENDPOINT.new_vendor_dashbord, formData);
      setDashboardData(response?.data || {});
    } catch (error) {
      console.log("new_vendor_dashbord", error);
      setDashboardData({});
    } finally {
      setLoadingState(false);
    }
  }, [
    authToken,
    postWithAuth,
    vendorToken,
  ]);

  const getVendorUserCount = useCallback(async () => {
    if (!authToken || !vendorToken) return;
    const formData = new FormData();
    formData.append("page", 1);
    formData.append("vendor_token", vendorToken);
    try {
      const response = await postWithAuth(API_ENDPOINT.get_vendor_contacts, formData);
      setVendorUserCount(
        toNumber(
          response?.data?.total_count ||
            response?.data?.total_items ||
            response?.data?.count ||
            response?.data?.results?.length,
        ),
      );
    } catch (error) {
      console.log("get_vendor_contacts", error);
      setVendorUserCount(0);
    }
  }, [authToken, postWithAuth, vendorToken]);

  useEffect(() => {
    getVendorUserCount();
  }, [getVendorUserCount]);

  useEffect(() => {
    if (!authToken || !vendorToken) return;
    getAnalyticsValues();
  }, [authToken, getAnalyticsValues, vendorToken]);

  const bid = dashboardData?.bid_submission || {};
  const bidSummary = bid?.summary || {};
  const po = dashboardData?.customer_po || {};
  const poSummary = po?.summary || {};
  const invoice = dashboardData?.invoice_reconciliation || {};
  const invoiceSummary = invoice?.summary || {};
  const fourWayBase = dashboardData?.four_way_matching || {};
  const fourWaySummary =
    fourWayBase?.payment_advance_summary ||
    fourWayBase?.payment_advance_metrics ||
    fourWayBase?.payment_advice_summary ||
    fourWayBase?.payment_advice_metrics ||
    fourWayBase?.summary ||
    fourWayBase;

  const currency =
    bidSummary.currency ||
    poSummary.currency ||
    fourWaySummary.currency ||
    invoiceSummary.currency ||
    "INR";

  const bidMonthRows = useMemo(
    () =>
      buildMonthRows(bid?.monthwise, (row) => ({
        total: toNumber(row.total_bid),
        submitted: toNumber(row.submitted_bid),
        success: toNumber(row.success_bid),
        notSubmitted: toNumber(row.not_submitted_bid),
      })),
    [bid?.monthwise],
  );

  const bidYearRows = useMemo(
    () =>
      (Array.isArray(bid?.yearwise) ? bid.yearwise : []).map((row) => ({
        label: row.year || "Unknown",
        total: toNumber(row.total_bid),
        submitted: toNumber(row.submitted_bid),
        success: toNumber(row.success_bid),
      })),
    [bid?.yearwise],
  );

  const poMonthRows = useMemo(
    () =>
      buildMonthRows(po?.monthwise, (row) => ({
        count: toNumber(row.po_count),
        budget: toNumber(row.po_budget),
      })),
    [po?.monthwise],
  );

  const invoiceMonthRows = useMemo(
    () =>
      buildMonthRows(invoice?.monthwise, (row) => ({
        count: toNumber(row.invoice_count),
        amount: toNumber(row.invoice_amount),
        matched: toNumber(row.matched_count),
        partial: toNumber(row.partially_matched_count),
        unverified: toNumber(row.unverified_count),
      })),
    [invoice?.monthwise],
  );

  const itemSpendRows = useMemo(
    () =>
      topRows(
        (Array.isArray(po?.itemwise_spend) ? po.itemwise_spend : []).map((row) => ({
          label: row.item_name || row.item_code || "Unknown item",
          spend: toNumber(row.spend),
          quantity: toNumber(row.quantity),
          poCount: toNumber(row.po_count),
        })),
        "spend",
      ),
    [po?.itemwise_spend],
  );

  const matchStatusRows = useMemo(
    () => objectToRows(invoice?.match_status_counts, "name", "value"),
    [invoice?.match_status_counts],
  );

  const approvalStatusRows = useMemo(
    () => objectToRows(invoice?.approval_status_counts, "name", "value"),
    [invoice?.approval_status_counts],
  );

  const buyerAmountRows = useMemo(
    () => topRows(objectToRows(invoice?.buyer_invoice_amounts, "label", "value"), "value", 5),
    [invoice?.buyer_invoice_amounts],
  );

  const fieldVerificationRows = useMemo(
    () =>
      Object.entries(invoice?.verification_field_counts || {}).map(([key, value]) => ({
        label: titleCase(key).replace("Po", "PO"),
        matched: toNumber(value?.matched),
        partial: toNumber(value?.partial),
        mismatch: toNumber(value?.mismatch),
        unknown: toNumber(value?.unknown),
      })),
    [invoice?.verification_field_counts],
  );

  const paymentStatusRows = useMemo(
    () =>
      [
        { name: "Fully Paid", value: toNumber(fourWaySummary?.fully_paid_count) },
        { name: "Partially Paid", value: toNumber(fourWaySummary?.partially_paid_count) },
        { name: "Unpaid", value: toNumber(fourWaySummary?.unpaid_count) },
      ].filter((row) => row.value > 0),
    [fourWaySummary?.fully_paid_count, fourWaySummary?.partially_paid_count, fourWaySummary?.unpaid_count],
  );

  const paymentModeRows = useMemo(
    () =>
      getObjectEntries(fourWaySummary?.payment_mode_breakup).map(([name, value]) => ({
        name,
        value: toNumber(value),
      })).filter((row) => row.value > 0),
    [fourWaySummary?.payment_mode_breakup],
  );

  const paymentMonthRows = useMemo(() => {
    const source = fourWaySummary?.monthwise_payment_advance || {};
    return getObjectEntries(source)
      .map(([period, value]) => ({
        label: formatMonthPeriod(period),
        period,
        recordCount: toNumber(value?.record_count),
        documentCount: toNumber(value?.document_count),
        paidAmount: toNumber(value?.paid_amount),
      }))
      .sort((a, b) => String(a.period).localeCompare(String(b.period)));
  }, [fourWaySummary?.monthwise_payment_advance]);

  const paymentYearRows = useMemo(() => {
    const source = fourWaySummary?.yearwise_payment_advance || {};
    return getObjectEntries(source)
      .map(([period, value]) => ({
        label: String(period),
        period,
        recordCount: toNumber(value?.record_count),
        documentCount: toNumber(value?.document_count),
        paidAmount: toNumber(value?.paid_amount),
      }))
      .sort((a, b) => String(a.period).localeCompare(String(b.period)));
  }, [fourWaySummary?.yearwise_payment_advance]);

  const recentFourWayRecords = useMemo(
    () => asArray(fourWayBase?.recent_records),
    [fourWayBase?.recent_records],
  );
  const paymentAdvicePageSize = 10;
  const paymentAdviceTotalPages = Math.max(
    1,
    Math.ceil(recentFourWayRecords.length / paymentAdvicePageSize),
  );
  const paginatedFourWayRecords = useMemo(() => {
    const startIndex = (paymentAdvicePage - 1) * paymentAdvicePageSize;
    return recentFourWayRecords.slice(startIndex, startIndex + paymentAdvicePageSize);
  }, [paymentAdvicePage, recentFourWayRecords]);

  useEffect(() => {
    setPaymentAdvicePage(1);
  }, [recentFourWayRecords.length]);

  const paymentInsightSummaryCards = [
    {title:"Total Records", value: fourWaySummary?.payment_advance_record_count, note:"Total records in payment advice", accent: T.brand},
    {title:"Advance Payment Records",value: fourWaySummary?.advance_payment_count, note:"Total documents in payment advice", accent: T.teal},

    { title: "Fully Paid", value: fourWaySummary?.fully_paid_count,note:"Total records processed", accent: T.success },
    { title: "Partially Paid", value: fourWaySummary?.partially_paid_count, note: "Records with partial payments", accent: T.warning },
    { title: "Unpaid", value: fourWaySummary?.unpaid_count, note: "Records with no payments", accent: T.danger },
     {
      title: "Payment Advice Invoice Value",
      value: formatPreciseCurrency(fourWaySummary?.payment_advance_invoice_total_amount, "INR"),
      note: "Invoice value from payment advice",
      accent: T.brand,
    },
    {
      title: "Payment Advance Total",
      value: formatPreciseCurrency(fourWaySummary?.payment_advance_total_amount, "INR"),
      note: "Payment advice paid total",
      accent: T.teal,
    },
   
    {
      title: "Payment Advice Outstanding",
      value: formatPreciseCurrency(fourWaySummary?.payment_advance_outstanding_amount, "INR"),
      note: "Outstanding from payment advice",
      accent: T.warning,
    },
  
    
    
  ];

  const metrics = [
    {
      title: "Total Bids",
      value: formatNumber(bidSummary.total_bid),
      note: `${formatNumber(bidSummary.submitted_bid)} submitted`,
      accent: T.brand,
      onClick: () => navigate("/Vendor_dashboard/RFQ"),
      tooltip: (
        <div style={{ display: "grid", gap: 7 }}>
          <p style={tooltipTextStyle}>
            Submitted: {formatNumber(bidSummary.submitted_bid)}. Not submitted:{" "}
            {formatNumber(bidSummary.not_submitted_bid)}.
          </p>
          {bidYearRows.length ? (
            <div style={{ display: "grid", gap: 5 }}>
              {bidYearRows.map((row) => (
                <div
                  key={row.label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    color: T.textSec,
                    fontFamily: T.fontSemiBold,
                    fontSize: 14,
                  }}
                >
                  <span>{row.label}</span>
                  <strong style={{ fontFamily: T.fontDisplay, color: T.text }}>
                    {formatNumber(row.total)} bids
                  </strong>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      title: "Successful Bids",
      value: formatNumber(bidSummary.success_bid),
      note: `${toPercent(bidSummary.success_bid, bidSummary.submitted_bid)} success rate`,
      accent: T.success,
      onClick: () => navigate("/Vendor_dashboard/RFQ"),
    },
    {
      title: "Selected Bid Value",
      value: formatCurrency(bidSummary.selected_bid_value, currency),
      note: "Awarded sourcing value",
      accent: T.teal,
      emphasis: true,
      onClick: () => navigate("/Vendor_dashboard/RFQ"),
    },
    {
      title: "Customer POs",
      value: formatNumber(poSummary.total_po_count),
      note: formatCurrency(poSummary.total_po_budget, currency),
      accent: T.purple,
      onClick: () => navigate("/Vendor_dashboard/PO"),
    },

   
    {
      title: "Invoices",
      value: formatNumber(invoiceSummary.total_invoice_count),
      note: `${formatNumber(invoiceSummary.verified_count)} verified`,
      accent: T.info,
      onClick: () => navigate("/Vendor_dashboard/InvoiceProcess"),
    },

       {
      title: "Invoice Reconciliation Total",
      value: formatPreciseCurrency(invoiceSummary?.total_invoice_amount, invoiceSummary?.currency || currency),
      note: "Total from invoice reconciliation",
      accent: T.info,
    },
    {
      title: "Payment Advice Records",
      value: formatNumber(fourWaySummary?.payment_advance_record_count),
      note: `${formatNumber(fourWaySummary?.payment_advance_document_count)} documents`,
      accent: T.success,
      onClick: scrollToPaymentAdviceRecords,
    },
  
    {
      title: "Supplier Users",
      value: formatNumber(vendorUserCount),
      note: "Active contact users",
      accent: T.pink,
      onClick: () => navigate("/Vendor_dashboard/Users"),
    },
  ];

  const vendorDashboardTabs = [
    { key: "bid", label: "Bid Submission", description: "Submitted, successful, and pending bid movement.", accent: T.brand },
    { key: "po", label: "Customer PO", description: "Customer order volume, budget, and item spend.", accent: T.purple },
    { key: "invoice", label: "Invoice Reconciliation", description: "Invoice matching, approval, and exposure health.", accent: T.info },
    { key: "payment", label: "Payment Advice", description: "Payment status, modes, and advice records.", accent: T.success },
  ];

  const activeModuleMetrics = {
    bid: [
      { title: "Total Bids", value: formatNumber(bidSummary.total_bid), note: "All bid invitations", accent: T.brand, onClick: () => navigate("/Vendor_dashboard/RFQ") },
      { title: "Submitted Bids", value: formatNumber(bidSummary.submitted_bid), note: "Submitted to customers", accent: T.info, onClick: () => navigate("/Vendor_dashboard/RFQ") },
      { title: "Not Submitted", value: formatNumber(bidSummary.not_submitted_bid), note: "Pending response", accent: T.warning, onClick: () => navigate("/Vendor_dashboard/RFQ") },
      { title: "Successful Bids", value: formatNumber(bidSummary.success_bid), note: `${toPercent(bidSummary.success_bid, bidSummary.submitted_bid)} success rate`, accent: T.success, onClick: () => navigate("/Vendor_dashboard/RFQ") },
      { title: "Not Successful", value: formatNumber(bidSummary.not_success_bid), note: "Submitted but not selected", accent: T.danger, onClick: () => navigate("/Vendor_dashboard/RFQ") },
      { title: "Selected Bid Value", value: formatCurrency(bidSummary.selected_bid_value, currency), note: "Awarded sourcing value", accent: T.teal, onClick: () => navigate("/Vendor_dashboard/RFQ") },
    ],
    po: [
      { title: "Customer POs", value: formatNumber(poSummary.total_po_count), note: "Total customer POs", accent: T.purple, onClick: () => navigate("/Vendor_dashboard/PO") },
      { title: "PO Budget", value: formatCurrency(poSummary.total_po_budget, currency), note: "Total PO value", accent: T.teal, onClick: () => navigate("/Vendor_dashboard/PO") },
    ],
    invoice: [
      { title: "Invoices", value: formatNumber(invoiceSummary.total_invoice_count), note: "Total invoice records", accent: T.info, onClick: () => navigate("/Vendor_dashboard/InvoiceProcess") },
      { title: "Verified", value: formatNumber(invoiceSummary.verified_count), note: "Verified invoices", accent: T.success, onClick: () => navigate("/Vendor_dashboard/InvoiceProcess") },
      { title: "Unverified", value: formatNumber(invoiceSummary.unverified_count), note: "Pending verification", accent: T.warning, onClick: () => navigate("/Vendor_dashboard/InvoiceProcess") },
      { title: "Invoice Reconciliation Total", value: formatPreciseCurrency(invoiceSummary?.total_invoice_amount, invoiceSummary?.currency || currency), note: "Total invoice amount", accent: T.info, onClick: () => navigate("/Vendor_dashboard/InvoiceProcess") },
    ],
    payment: paymentInsightSummaryCards.map((card) => ({ ...card, onClick: scrollToPaymentAdviceRecords })),
  }[activeVendorModule] || metrics;

  return (
    <div
      className={classes.dashboardPage}
      style={{ background: T.bg, fontFamily: T.fontBody, fontSize: 14 }}
    >
      <div className={classes.pageSectionPad}>
        <HeaderTitle titleText="Customer Command Center" />
      </div>

      <div className={classes.pageSectionPadMain}>
        <div className={classes.dashboardGroup}>
          <ControlTowerBanner
            currency={currency}
            bidSummary={bidSummary}
            poSummary={poSummary}
            invoiceSummary={invoiceSummary}
          />

          <div className={classes.tabNavInner}>
            <div className={classes.tabNavLabel}>DASHBOARD SECTIONS</div>
            <div className={classes.tabNavTabs}>
              {vendorDashboardTabs.map((tab) => {
              const active = activeVendorModule === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveVendorModule(tab.key)}
                  className={`${classes.tabBtn} ${active ? classes.tabBtnActive : ""}`}
                  style={{ "--tab-accent": tab.accent, scrollSnapAlign: "start" }}
                >
                  <div className={classes.tabBtnTop}>
                    <span className={classes.tabBtnDot} />
                    <span className={classes.tabBtnLabel}>{tab.label}</span>
                  </div>
                  <div className={classes.tabBtnDesc}>{tab.description}</div>
                </button>
              );
            })}
            </div>
          </div>

          <div style={metricGridStyle}>
            {activeModuleMetrics.map((metric) => (
              <MetricCard key={metric.title} {...metric} />
            ))}
          </div>

          {activeVendorModule === "bid" ? <SectionCard
            title="Bid Submission Intelligence"
            subtitle="Sourcing participation, submitted bids, and award conversion."
          >
            <div style={wideSplitGridStyle}>
              <div style={chartShellStyle}>
                <BarPanel
                  title="Monthly Bid Performance"
                  note="Total, submitted, and successful bids from the new vendor dashboard API."
                  data={bidMonthRows}
                  onClick={() => navigate("/Vendor_dashboard/RFQ")}
                  trendFilter={true}
                  bars={[
                    { key: "total", name: "Total Bids", color: T.brand },
                    { key: "submitted", name: "Submitted", color: T.info },
                    { key: "success", name: "Successful", color: T.success },
                  ]}
                />
              </div>
              <div style={chartShellStyle}>
                <DonutPanel
                  title="Bid Outcome Split"
                  note="Submitted, not submitted, successful, and not successful bid counts."
                  data={[
                    { name: "Submitted", value: bidSummary.submitted_bid },
                    { name: "Not Submitted", value: bidSummary.not_submitted_bid },
                    { name: "Successful", value: bidSummary.success_bid },
                    { name: "Not Successful", value: bidSummary.not_success_bid },
                  ].filter((row) => toNumber(row.value) > 0)}
                  onClick={() => navigate("/Vendor_dashboard/RFQ")}
                />
              </div>
            </div>
          </SectionCard> : null}

          {activeVendorModule === "po" ? <SectionCard
            title="Customer PO Analytics"
            subtitle="Customer purchase order volume, value, and item-level spend concentration."
          >
            <div style={wideSplitGridStyle}>
              <div style={chartShellStyle}>
                <LinePanel
                  title="PO Count Trend"
                  note="Monthly customer PO count."
                  data={poMonthRows}
                  onClick={() => navigate("/Vendor_dashboard/PO")}
                  trendFilter={true}
                  lines={[{ key: "count", name: "PO Count", color: T.purple }]}
                />
              </div>
              <div style={chartShellStyle}>
                <BarPanel
                  title="PO Budget Trend"
                  note="Monthly PO budget value."
                  data={poMonthRows}
                  onClick={() => navigate("/Vendor_dashboard/PO")}
                  formatter={(value) => formatCurrency(value, currency)}
                  trendFilter={true}
                  bars={[{ key: "budget", name: "PO Budget", color: T.teal }]}
                />
              </div>
            </div>
            <div style={{ marginTop: 20 }}>
              <div style={chartShellStyle}>
                <HorizontalBarPanel
                  title="Top Items by PO Spend"
                  note="Highest value items, limited to keep the view readable."
                  data={itemSpendRows}
                  valueKey="spend"
                  formatter={(value) => formatCurrency(value, currency)}
                  onClick={() => navigate("/Vendor_dashboard/PO")}
                />
              </div>
            </div>
          </SectionCard> : null}

          {activeVendorModule === "invoice" ? <SectionCard
            title="Invoice Reconciliation"
            subtitle="Invoice verification, matching quality, and buyer-wise invoice exposure."
          >
            <div style={wideSplitGridStyle}>
              <div style={chartShellStyle}>
                <DonutPanel
                  title="Match Status"
                  note="Matched, partially matched, mismatched, and unverified invoices."
                  data={matchStatusRows.filter((row) => row.value > 0)}
                  onClick={() => navigate("/Vendor_dashboard/InvoiceProcess")}
                />
              </div>
              <div style={chartShellStyle}>
                <BarPanel
                  title="Verification Field Health"
                  note="Field-level match confidence from invoice reconciliation."
                  data={fieldVerificationRows}
                  onClick={() => navigate("/Vendor_dashboard/InvoiceProcess")}
                  bars={[
                    { key: "matched", name: "Matched", color: FIELD_COLORS[0] },
                    { key: "partial", name: "Partial", color: FIELD_COLORS[1] },
                    { key: "mismatch", name: "Mismatch", color: FIELD_COLORS[2] },
                    { key: "unknown", name: "Unknown", color: FIELD_COLORS[3] },
                  ]}
                />
              </div>
            </div>
            <div style={{ ...wideSplitGridStyle, marginTop: 20 }}>
              <div style={chartShellStyle}>
                <LinePanel
                  title="Invoice Amount Trend"
                  note="Monthly invoice value and verified match count."
                  data={invoiceMonthRows}
                  onClick={() => navigate("/Vendor_dashboard/InvoiceProcess")}
                  formatter={(value, item) =>
                    item?.dataKey === "amount" ? formatCurrency(value, currency) : formatNumber(value)
                  }
                  trendFilter={true}
                  lines={[
                    { key: "amount", name: "Invoice Amount", color: T.info },
                    { key: "matched", name: "Matched Count", color: T.success },
                  ]}
                />
              </div>
              <div style={chartShellStyle}>
                <ChartTitle title="Buyer Invoice Exposure" note="Top invoice amount contributors." />
                <InsightList rows={buyerAmountRows} formatter={(value) => formatCurrency(value, currency)} />
              </div>
            </div>
            <div style={{ marginTop: 20 }}>
              <div style={chartShellStyle}>
                <DonutPanel
                  title="Approval Status"
                  note="Invoice approval pipeline status."
                  data={approvalStatusRows.filter((row) => row.value > 0)}
                  onClick={() => navigate("/Vendor_dashboard/InvoiceProcess")}
                />
              </div>
            </div>
          </SectionCard> : null}

          {activeVendorModule === "payment" ? <SectionCard
            title="Customer Payment Advice"
            subtitle="Customer payment status, settlement modes, and payment advice records."
          >
            <div>
              <div style={wideSplitGridStyle}>
                <div style={chartShellStyle}>
                  <DonutPanel
                    title="Payment Status"
                    note="Fully paid, partially paid, and unpaid reconciliation counts."
                    data={paymentStatusRows}
                    onClick={undefined}
                  />
                </div>
                <div style={chartShellStyle}>
                  <DonutPanel
                    title="Payment Mode"
                    note="Payment mode breakup returned by the API."
                    data={paymentModeRows}
                    onClick={undefined}
                  />
                </div>
              </div>

              <div style={{ marginTop: 20 }}>
                <div style={chartShellStyle}>
                  <PaymentTrendPanel
                    title="Monthly / Yearly Payment Advice"
                    note="Record count, document count, and paid amount by period."
                    monthlyRows={paymentMonthRows}
                    yearlyRows={paymentYearRows}
                    currency={currency}
                  />
                </div>
              </div>

              <div ref={paymentAdviceRecordsRef} style={{ marginTop: 20, display: "grid", gap: 14, scrollMarginTop: 88 }}>
                <ChartTitle
                  title="Payment Advice Records"
                  note="Every recent payment advice record returned by Four Way Matching recent records."
                />
                {recentFourWayRecords.length ? (
                  <>
                    <PaymentAdviceRecordsTable
                      records={paginatedFourWayRecords}
                      currency={currency}
                      onViewDetails={setSelectedFourWayRecord}
                    />
                    {recentFourWayRecords.length > paymentAdvicePageSize ? (
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 14px", border: `1px solid ${T.border}`, borderRadius: 12, background: "linear-gradient(90deg, rgba(79,124,255,0.08), rgba(14,165,233,0.06))" }}>
                        <span style={{ color: T.textSec, fontFamily: T.fontSemiBold, fontSize: 13 }}>
                          Showing {(paymentAdvicePage - 1) * paymentAdvicePageSize + 1}-{Math.min(paymentAdvicePage * paymentAdvicePageSize, recentFourWayRecords.length)} of {recentFourWayRecords.length}
                        </span>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <button
                            type="button"
                            disabled={paymentAdvicePage === 1}
                            onClick={() => setPaymentAdvicePage((page) => Math.max(1, page - 1))}
                            style={{ border: `1px solid ${T.border}`, borderRadius: 10, background: paymentAdvicePage === 1 ? "#F1F5F9" : "#fff", color: paymentAdvicePage === 1 ? "#94A3B8" : T.text, padding: "8px 12px", fontFamily: T.fontSemiBold, fontSize: 13, cursor: paymentAdvicePage === 1 ? "not-allowed" : "pointer" }}
                          >
                            Previous
                          </button>
                          <span style={{ color: T.text, fontFamily: T.fontDisplay, fontSize: 13 }}>
                            Page {paymentAdvicePage} of {paymentAdviceTotalPages}
                          </span>
                          <button
                            type="button"
                            disabled={paymentAdvicePage === paymentAdviceTotalPages}
                            onClick={() => setPaymentAdvicePage((page) => Math.min(paymentAdviceTotalPages, page + 1))}
                            style={{ border: `1px solid ${T.border}`, borderRadius: 10, background: paymentAdvicePage === paymentAdviceTotalPages ? "#F1F5F9" : T.brand, color: paymentAdvicePage === paymentAdviceTotalPages ? "#94A3B8" : "#fff", padding: "8px 12px", fontFamily: T.fontSemiBold, fontSize: 13, cursor: paymentAdvicePage === paymentAdviceTotalPages ? "not-allowed" : "pointer" }}
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <EmptyChart label="No recent reconciliation records available" />
                )}
              </div>
            </div>
          </SectionCard> : null}

        </div>
      </div>

      <ReconciliationDetailsDrawer
        open={Boolean(selectedFourWayRecord)}
        onClose={() => setSelectedFourWayRecord(null)}
        record={selectedFourWayRecord}
        currency={currency}
      />
    </div>
  );
};

export default VendorAdminDashboardTab;
